// S-3 `/signin` — Вход. Source: stage-4-screens.md §5.3.
//
// Канонический шаблон login (см. §2.2 race protection):
//   const r = await loginMutation.mutateAsync({email,password});
//   await setToken(r.token);
//   await queryClient.invalidateQueries({queryKey: queryKeys.profileMe});
//   → OnboardingRouter роутит по свежему useOnboardingState
//
// 403 unverified (D5) → backend уже отправил код + вернул verification_challenge →
//   AwaitVerifyContext.set({mode:'signin', verificationChallenge}) → /verify-code
// 422 → setError per поле

import { Link, useLocation } from 'wouter';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { SigninSchema, type SigninForm } from '@/api/schemas';
import type { LoginVerifyRequiredResponse } from '@/api/types';
import { useLoginMutation } from '@/api/hooks/auth';
import { queryKeys } from '@/api/queryKeys';
import { applyValidationErrors, isApiError, useApiErrorToast } from '@/api/error';
import { useAwaitVerifyContext } from '@/context/AwaitVerifyContext';
import { setToken } from '@/lib/auth-storage';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { FormField } from '@/components/forms/FormField';
import { SubmitButton } from '@/components/forms/SubmitButton';

export function Signin() {
  const [, setLocation] = useLocation();
  const loginMutation = useLoginMutation();
  const queryClient = useQueryClient();
  const awaitVerify = useAwaitVerifyContext();
  const showErrorToast = useApiErrorToast();

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors },
  } = useForm<SigninForm>({
    resolver: zodResolver(SigninSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    try {
      const response = await loginMutation.mutateAsync({ email, password });
      await setToken(response.token);
      await queryClient.invalidateQueries({ queryKey: queryKeys.profileMe });
      // Дальше OnboardingRouter (на /) или RequireConsent сами разрулят. Push на корень:
      setLocation('/');
    } catch (err) {
      // D5: ведём на OTP-экран только при явном verify_required + challenge.
      // Прочие 403 (или 403 без этих полей) → обычный error-path, не brittle-редирект.
      if (isApiError(err) && err.response?.status === 403) {
        const body = err.response?.data as Partial<LoginVerifyRequiredResponse> | undefined;
        if (body?.verify_required === true && body.verification_challenge) {
          const current = getValues();
          const challenge = body.verification_challenge;
          flushSync(() => {
            awaitVerify.set({
              mode: 'signin',
              email: current.email,
              verificationChallenge: challenge,
            });
          });
          setLocation('/verify-code');
          return;
        }
      }
      const applied = applyValidationErrors<keyof SigninForm>(err, setError);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <ScreenHeader onBack={() => setLocation('/')} title="Назад" />

      {/* Gradient-hero + blur-blob (палитра лендинга, как на /verify-code). */}
      <div className="relative flex-1 overflow-y-auto overflow-x-hidden bg-gradient-to-b from-primary/5 to-transparent px-6 pb-8 pt-2">
        <div className="pointer-events-none absolute -right-16 -top-10 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-surface bg-gradient-to-br from-primary to-primary/70 text-xl font-bold text-primary-foreground shadow-lg shadow-primary/25">
              H
            </div>
            <h1 className="mb-1 text-hl-title font-bold leading-tight tracking-tight">HealthLab</h1>
            <p className="text-sm text-muted-foreground">Войдите в свой аккаунт</p>
          </div>

        <div className="mb-5 flex rounded-control bg-muted p-1">
          <div className="flex-1 rounded-md bg-background px-4 py-2 text-center text-sm font-medium shadow-sm">
            Войти
          </div>
          <Link
            href="/signup"
            className="flex-1 rounded-md px-4 py-2 text-center text-sm font-medium text-muted-foreground"
          >
            Зарегистрироваться
          </Link>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormField
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="ваш@email.com"
            error={errors.email?.message}
            {...register('email')}
          />
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Пароль</span>
              <Link href="/forgot-password" className="text-hl-small font-semibold text-primary-text">
                Забыли пароль?
              </Link>
            </div>
            <FormField
              type="password"
              autoComplete="current-password"
              placeholder="Ваш пароль"
              error={errors.password?.message}
              {...register('password')}
            />
          </div>
          <SubmitButton isPending={loginMutation.isPending}>Войти</SubmitButton>
        </form>

          <p className="mt-5 text-center text-hl-small text-muted-foreground">
            Нет аккаунта?{' '}
            <Link href="/signup" className="font-semibold text-primary-text">
              Создать
            </Link>
          </p>

          {/* HEAL-3: документы должны открываться и с экрана авторизации. */}
          <p className="mt-4 text-center text-hl-small leading-snug text-muted-foreground">
            <Link href="/legal/privacy" className="underline-offset-2 hover:underline">
              Политика конфиденциальности
            </Link>
            {' · '}
            <Link href="/legal/tos" className="underline-offset-2 hover:underline">
              Условия использования
            </Link>
          </p>
        </div>
      </div>
    </MobileFrame>
  );
}
